import { Col, Row, Text } from 'components';
import { fetchPointAdjustments, formatLocale, theme } from 'lib';
import { useEffect, useState } from 'react';
import GloryBoardCard from './GloryBoardCard';
import { REASON_OPTIONS } from './pointAdjustment';

/**
 * What has already been done to this participant's balance on this team.
 *
 * Strictly read-only, and deliberately so: an adjustment is reversible only by another adjustment,
 * so a control here that appeared to undo one would be lying. It is also not the reward history —
 * that one is the fan's earning ledger and carries no reason, because the reason is the
 * administrator's and never shown to the participant.
 *
 * It sits in the column beside the adjustment form, which is roughly half the panel wide. A
 * five-column table does not survive that width, so each record is stacked instead: the amount and
 * the reason on the first line, and who did it, when, and what the balance became underneath. The
 * same five facts, in the order someone checking a balance reads them.
 */

/** The label for a stored code, or the code itself when it is one the list no longer offers. */
const reasonLabel = (code) => REASON_OPTIONS.find((option) => option.code === code)?.value || code;

const ReasonPill = ({ code }) => (
  <div
    style={{
      backgroundColor: theme.GREY_10,
      padding: '3px 10px',
      borderRadius: '12px',
      flexShrink: 0
    }}
  >
    <Text tiny med col={theme.GREY_60}>
      {reasonLabel(code)}
    </Text>
  </div>
);

const AdjustmentRow = ({ item, last }) => (
  <Col
    noFlex
    wid="100%"
    pad={last ? '12px 0 0' : '12px 0'}
    borderBottom={last ? undefined : `1px solid ${theme.GREY_10}`}
  >
    <Row between center wid="100%" gap="8px">
      {/* The sign is part of the figure: every adjustment this screen can make subtracts. */}
      <Text h4 bold col={theme.RED}>
        −{Number(item.points || 0).toLocaleString()}
      </Text>

      <ReasonPill code={item.reasonCode} />
    </Row>

    <Row between center wid="100%" gap="8px" marg="6px 0 0">
      <Col noFlex minWid="0">
        <Text c1 col={theme.GREY_60}>
          {formatLocale(item.createdAt, 'dd MMM yyyy HH:mm')}
        </Text>

        <Row start center noFlex gap="4px">
          {/* The account may be gone; the record still names who did it. */}
          <Text c1 col={theme.GREY_60}>
            {item.administrator?.name || item.administrator?.email || 'Unknown'}
          </Text>

          {item.administrator?.deleted && (
            <Text tiny col={theme.GREY_40}>
              (account deleted)
            </Text>
          )}
        </Row>
      </Col>

      <Col noFlex endAll>
        <Text tiny col={theme.GREY_60}>
          Balance
        </Text>
        <Text b2 med>
          {Number(item.balanceAfter ?? 0).toLocaleString()}
        </Text>
      </Col>
    </Row>
  </Col>
);

const GloryBoardAdjustmentHistory = ({ teamId, userId, refreshToken }) => {
  const [state, setState] = useState({ status: 'loading', items: [] });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setState({ status: 'loading', items: [] });

      try {
        // `{ silent: true }` means the wrapper normally returns a falsy value rather than throwing,
        // but the try/catch stays: a component that leaves a rejection unhandled takes the whole
        // tab down, and this panel is never the most important thing on the screen.
        const res = await fetchPointAdjustments({ teamId, userId });

        if (cancelled) return;

        if (!res?.data) {
          setState({ status: 'error', items: [] });
          return;
        }

        setState({ status: 'ready', items: res.data.items || [] });
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setState({ status: 'error', items: [] });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [teamId, userId, refreshToken]);

  return (
    <GloryBoardCard
      title="Adjustment History"
      subtitle="Point adjustments made by administrators. Records are kept for 90 days."
    >
      {state.status === 'loading' && <Text col={theme.GREY_60}>Loading…</Text>}

      {state.status === 'error' && <Text col={theme.RED}>Adjustment history unavailable.</Text>}

      {state.status === 'ready' && state.items.length === 0 && (
        <Text col={theme.GREY_60}>No adjustments have been made to this participant.</Text>
      )}

      {state.status === 'ready' && state.items.length > 0 && (
        // Capped and scrolled rather than allowed to run on: the list is up to 50 records long and
        // the ranking below it is the other half of this screen's job.
        <Col start noFlex wid="100%" maxHt="260px" overflow="auto">
          {state.items.map((item, i) => (
            <AdjustmentRow key={item._id} item={item} last={i === state.items.length - 1} />
          ))}
        </Col>
      )}
    </GloryBoardCard>
  );
};

export default GloryBoardAdjustmentHistory;
